<template>
    <div class="container ev-charge-calculator">
        <h3 class="ev-charge-calculator__title"> Used Vehicle Charging Made Simple </h3>
        <p>
            We’ve made it easy to see what charging this {{ vehicle.model }} really looks like.
            You can now view the estimated cost to charge it from empty,
            plus how long it’ll take to go from 0% to 100%; whether you're at home or using a public charger.
            No guesswork, just simple, useful info to help you drive electric with confidence.
        </p>

        <div class="ev-charge-section rd-card">
            <fieldset class="ev-charge-section--container" v-if="homeChargingTimes.length > 0">
                <legend class="ev-charge-section--title">
                    Home Charging
                </legend>

                <div class="ev-charge-section--content">
                    <div class="ev-charge-section--content__left">
                        <select class="form-element__input" name="home_charging" id="home_charging" v-model="homeChargingId">
                            <option v-for="chargingTime in homeChargingTimes" :key="chargingTime.id" :value="chargingTime.id">
                                {{ formatHomeChargingOptionName(chargingTime) }}
                            </option>
                        </select>
                        <div class="ev-charge-section--content__charge-time" v-if="selectedHomeCharging && selectedHomeCharging.end_battery_percentage">
                            <h3>Full Charge, up to {{ selectedHomeCharging.end_battery_percentage }}%,</h3>
                            {{ formatChargeTime(selectedHomeCharging) }}
                        </div>
                    </div>
                    <div class="ev-charge-section--content__right">
                        <div class="ev-charge-section--content__charge-cost" v-if="selectedHomeCharging && selectedHomeCharging.end_battery_percentage">
                            <h3>Estimated Cost To Charge</h3>
                            {{ formatHomeChargingCost() }}
                        </div>
                    </div>
                </div>
            </fieldset>

            <fieldset class="ev-charge-section--container" v-if="publicChargingTimes.length > 0">
                <legend class="ev-charge-section--title">
                    Public Charging
                </legend>

                <div class="ev-charge-section--content">
                    <div class="ev-charge-section--content__left">
                        <select class="form-element__input" name="public_charging" id="public_charging" v-model="publicChargingId">
                            <option v-for="chargingTime in publicChargingTimes" :key="chargingTime.id" :value="chargingTime.id">
                                {{ chargingTime.charger_description }} - {{ chargingTime.charger_type }}
                            </option>
                        </select>
                        <div class="ev-charge-section--content__charge-time" v-if="selectedPublicCharging && selectedPublicCharging.end_battery_percentage">
                            <h3>Full Charge, up to {{ selectedPublicCharging.end_battery_percentage }}%,</h3>
                            {{ formatChargeTime(selectedPublicCharging) }}
                        </div>
                    </div>
                    <div class="ev-charge-section--content__right">
                        <div class="ev-charge-section--content__charge-cost" v-if="selectedPublicCharging && selectedPublicCharging.end_battery_percentage">
                            <h3>Estimated Cost To Charge</h3>
                            {{ formatPublicChargingCost() }}
                        </div>
                    </div>
                </div>
            </fieldset>
        </div>

        <p class="ev-charge-calculator__terms">Charging times are based on manufacturer data and provided as a guide only, with some charging speeds potentially unavailable. Estimated charging costs are calculated using the vehicle’s battery capacity and average electricity prices: 60p per kWh for Rapid chargers, 79p per kWh for Ultra Rapid chargers, and a combination of 7p per kWh off-peak and 27p per kWh standard rates for home charging. These costs are for guidance only and may change over time. Actual energy costs can vary depending on factors such as driving style, tyre pressure, vehicle load, use of climate control, fuel type, road conditions, and your specific electricity tariff.</p>
    </div>
</template>

<script>
export default {
    props: {
        vehicle: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            homeChargingId: null,
            publicChargingId: null,
            batteryCapacity: this.vehicle.battery_capacity_kwh ?? this.vehicle.battery_usable_capacity_kwh,
        };
    },
    computed: {
        chargingTimes() {
            return this.vehicle.autotrader_used_vehicle_charge_times;
        },
        homeChargingTimes() {
            const filteredList = this.chargingTimes.filter((chargingTime) => chargingTime.connector_type === 'Type 2');

            return this.orderChargeTimeAscending(filteredList);
        },
        publicChargingTimes() {
            const filteredList = this.chargingTimes.filter((chargingTime) => chargingTime.connector_type === 'CCS');

            return this.orderChargeTimeAscending(filteredList);
        },
        selectedHomeCharging() {
            return this.homeChargingId ? this.findChargeTimeById(this.homeChargingId) : null;
        },
        selectedPublicCharging() {
            return this.publicChargingId ? this.findChargeTimeById(this.publicChargingId) : null;
        },
    },
    methods: {
        findChargeTimeById(chargeTimeId) {
            return this.chargingTimes.find((chargingTime) => chargingTime.id === chargeTimeId);
        },
        formatChargeTime(chargingTime) {
            const duration = chargingTime.duration_minutes;
            const hours = Math.floor(duration / 60);
            const minutes = duration % 60;
            return `${hours} hours ${minutes} minutes`;
        },
        formatHomeChargingCost() {
            const maxChargeAvailable = this.vehicle.battery_capacity_kwh * this.selectedHomeCharging.end_battery_percentage / 100;
            const chargeRate = this.getChargeRateFromChargingTime(this.selectedHomeCharging);

            const discountedChargeCost = 7; // 7p per kWh for home charging
            const discountedChargeDuration = 4; // 4 hours at 7p per kWh
            const totalDiscountedKwhCharged = Math.min(maxChargeAvailable, discountedChargeDuration * chargeRate);
            const totalDiscountedCost = totalDiscountedKwhCharged * discountedChargeCost / 100;

            const remainingKwhCharged = maxChargeAvailable - totalDiscountedKwhCharged;
            const standardChargeCost = 27;
            const totalStandardCost = remainingKwhCharged * standardChargeCost / 100;

            return this.formatChargingCost(totalDiscountedCost + totalStandardCost);
        },
        formatPublicChargingCost() {
            const totalKwhCharged = this.vehicle.battery_capacity_kwh * this.selectedPublicCharging.end_battery_percentage / 100;
            const isUltraRapid = this.selectedPublicCharging.charger_type.includes('Ultra') || this.selectedPublicCharging.charger_description.startsWith('100');
            const costPerKwh = isUltraRapid ? 79 : 60;
            const totalCost = totalKwhCharged * costPerKwh / 100;

            return this.formatChargingCost(totalCost);
        },
        formatChargingCost(cost) {
            return `£${cost.toFixed(2)}`;
        },
        formatHomeChargingOptionName(option) {
            const chargeRate = this.getChargeRateFromChargingTime(option);
            let description;
            if (chargeRate < 3) {
                description = 'Plug Socket';
            } else if (chargeRate < 4) {
                description = 'Slow Home Charger';
            } else if (chargeRate < 8) {
                description = 'Fast Home Charger';
            } else {
                description = 'Rapid Home Charger';
            }

            return `${option.charger_description} - ${description}`;
        },
        orderChargeTimeAscending(list) {
            return list.sort((a, b) => {
                const aDescription = this.getChargeRateFromChargingTime(a);
                const bDescription = this.getChargeRateFromChargingTime(b);
                return aDescription - bDescription;
            });
        },
        getChargeRateFromChargingTime(chargingTime) {
            if (!chargingTime || !chargingTime.charger_description) {
                return 0;
            }

            const chargeRate = parseFloat(chargingTime.charger_description.split(' ')[0]);
            return isNaN(chargeRate) ? 0 : chargeRate;
        },
    },
    mounted() {
        if (this.homeChargingTimes && this.homeChargingTimes.length) {
            const defaultHomeCharging = this.homeChargingTimes[this.homeChargingTimes.length - 1] ?? null
            this.homeChargingId = defaultHomeCharging ? defaultHomeCharging.id : null;
        }

        if (this.publicChargingTimes && this.publicChargingTimes.length) {
            const defaultPublicCharging = this.publicChargingTimes[0] ?? null;
            this.publicChargingId = defaultPublicCharging ? defaultPublicCharging.id : null;
        }
    },
};
</script>

<style lang="scss" scoped>
@import "../../../../sass/redesign/variables";

.ev-charge-calculator {
    &__title {
        font-size: 16px;
        font-weight: 700;
        margin-bottom: 10px;
        color: #243782;
    }

    &__terms {
        font-style: italic;
        font-size: 11px;
        font-weight: normal;
        line-height: 14px;
        color: #909090;
    }
}

.ev-charge-section {
    background: $blue-gradient-dark-to-light-vertical;
    color: white;
    display: flex;
    flex-direction: row;
    gap: 20px;

    @media screen and (max-width: $screen-md) {
        flex-direction: column;
    }

    &--container {
        border-radius: 10px;
        border: 0.5px solid $blue;
        padding: 20px;
        position: relative;
        width: 100%;
    }

    &--title {
        display: inline-block;
        font-size: 18px;
        font-weight: 700;
        padding-left: 15px;
        padding-right: 15px;
    }

    &--content {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 20px;
        width: 100%;

        @media screen and (max-width: $screen-md) {
            flex-direction: column;
            display: flex;
        }

        &__left {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        &__right {
            width: 100%;
        }

        &__charge-time {
            h3 {
                color: white;
                margin-bottom: 5px;
            }

            @media screen and (max-width: $screen-md) {
                justify-items: center;
                font-size: 20px;
                font-weight: 700;

                h3 {
                    color: white;
                    font-size: 16px;
                    margin-bottom: 5px;
                }
            }
        }

        &__charge-cost {
            h3 {
                color: white;
                font-size: 16px;
                margin-bottom: 5px;
            }

            justify-items: center;
            font-size: 20px;
            font-weight: 700;
        }
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/used-vue/components/UsedDetails/ElectricVehicleChargeCalculator.vue